import React, { useEffect, useMemo, useRef, useState } from "react"
import { Container, Row, Col, Card, Nav, Form, ListGroup, Button, DropdownButton, Dropdown } from "react-bootstrap"
import { toast } from "react-toastify"
import { HiOutlineMenu, HiOutlineRefresh, HiPencil, HiTrash, HiLockClosed } from "react-icons/hi"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"
import { Formik } from "formik"
import { DragDropProvider } from "@dnd-kit/react"
import { useSortable } from "@dnd-kit/react/sortable"
import { move } from "@dnd-kit/helpers"
import { RestrictToVerticalAxis, disableDropAnimation } from "../../../../helpers/dndkit"
import { useSelectedProject } from "../../../../contexts/SelectedProjectContext"
import { useGuides, useUpdateGuide, useAddImageToGuide, useAddVideoToGuide } from "../../../../hooks/useGuides"
import IonIcon from "../../../../components/ionIcon"
import FileUploader from "../../../../components/fileUploader"
import ConfirmationModal from "../../../../components/confirmation"
import { getLocaleValue } from "../../../../helpers/getLocaleValue"
import { GuideImage, GuideVideo, GuideVideoThumbnail, GuideModal } from "../components"
import { MAX_IMAGE_FILE_SIZE, MAX_VIDEO_FILE_SIZE } from "../../../../constants/fileHandling"
import { GuideTextList } from "../components/text"
import type { ContentBlock, GuideImageData, VideoData } from "../../../../constants/types"

type ConfirmationState = {
  show: boolean
  callback: (() => void) | null
  title: string
  content: string
  danger: boolean
}

const CONFIRMATION_DEFAULT: ConfirmationState = {
  show: false,
  callback: null,
  title: "",
  content: "",
  danger: false
}

type SortableContentItemProps = {
  id: string
  index: number
  block: ContentBlock
  language: string
  selected: boolean
  onClick: () => void
}

const SortableContentItem: React.FC<SortableContentItemProps> = ({ id, index, block, language, selected, onClick }) => {
  const { ref, isDragging } = useSortable({ id, index })

  return (
    <div ref={ref}>
      <ListGroup.Item
        className={selected ? "selected" : ""}
        style={isDragging ? { boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)" } : undefined}
        onClick={onClick}>
        <div className="round-shadow-btn">
          <HiOutlineMenu />
        </div>
        {(() => {
          switch (block.type) {
            case "header":
            case "paragraph":
              return <GuideTextList data={block.data as never} language={language} type={block.type} />
            case "image":
              return <GuideImage data={block.data as GuideImageData} list preview={false} full={false} />
            case "video":
              return <GuideVideoThumbnail data={block.data as VideoData} list preview={false} onClick={undefined} />
            default:
              return null
          }
        })()}
      </ListGroup.Item>
    </div>
  )
}

type BlockWithId = ContentBlock & { id: string }

const addBlockIds = (blocks: ContentBlock[]): BlockWithId[] => blocks.map((b, i) => ({ ...b, id: String(i) }))

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const stripBlockIds = (blocks: BlockWithId[]): ContentBlock[] => blocks.map(({ id, ...rest }) => rest)

const EditGuideContainer: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const { t, i18n } = useTranslation()

  const { selectedDestinationId } = useSelectedProject()
  const { data: guides, isLoading } = useGuides(selectedDestinationId)
  const updateGuideMutation = useUpdateGuide()
  const addImageMutation = useAddImageToGuide()
  const addVideoMutation = useAddVideoToGuide()

  const [language, setLanguage] = useState(i18n.resolvedLanguage ?? "en")
  const [selectedContentIndex, setSelectedContentIndex] = useState<number | null>(null)
  const [selectedContent, setSelectedContent] = useState<Record<string, unknown> | null>(null)
  const [selectedType, setSelectedType] = useState<string | null>(null)
  const [showEditTitleModal, setShowEditTitleModal] = useState(false)
  const [confirmation, setConfirmation] = useState<ConfirmationState>(CONFIRMATION_DEFAULT)

  const formRef = useRef<{ dirty: boolean } | null>(null)

  const guide = (guides ?? []).find(x => x.id === id)
  const serverBlocks = useMemo(() => guide?.content?.blocks ?? [], [guide?.content?.blocks])
  const [blocks, setBlocks] = useState<BlockWithId[]>(() => addBlockIds(serverBlocks))

  useEffect(() => {
    setBlocks(addBlockIds(serverBlocks))
  }, [serverBlocks])

  const hideConfirmation = () => setConfirmation(CONFIRMATION_DEFAULT)

  const selectContent = (index: number, block: ContentBlock) => {
    setSelectedContentIndex(index)
    setSelectedContent(block.data)
    setSelectedType(block.type)
  }

  const executeSortEnd = async (reordered: BlockWithId[]) => {
    if (!guide?.id) {
      toast.error(t("guides.notifications.sort.failed"))
      return
    }

    setBlocks(reordered)

    if (selectedContentIndex !== null) {
      const selectedBlock = blocks[selectedContentIndex]
      const newIndex = reordered.indexOf(selectedBlock)
      if (newIndex !== -1) setSelectedContentIndex(newIndex)
    }

    const updatedGuide = { ...guide, content: { ...guide.content, blocks: stripBlockIds(reordered) } }

    try {
      await updateGuideMutation.mutateAsync({ id: guide.id, data: updatedGuide })
    } catch {
      setBlocks(addBlockIds(serverBlocks))
      toast.error(t("guides.notifications.sort.failed"))
    }
  }

  const handleSortEnd = (reordered: BlockWithId[]) => {
    if (reordered.every((block, i) => block === blocks[i])) return

    if (!formRef.current?.dirty) {
      executeSortEnd(reordered)
    } else {
      setConfirmation({
        show: true,
        callback: () => {
          hideConfirmation()
          executeSortEnd(reordered)
        },
        title: "dirty",
        content: "dirty",
        danger: false
      })
    }
  }

  const handleSelectContent = (index: number, block: ContentBlock) => {
    if (!formRef.current?.dirty) {
      selectContent(index, block)
    } else {
      setConfirmation({
        show: true,
        callback: () => {
          hideConfirmation()
          selectContent(index, block)
        },
        title: "dirty",
        content: "dirty",
        danger: false
      })
    }
  }

  const handleSaveContent = async (
    values: Record<string, unknown>,
    resetForm: (opts: { values: Record<string, unknown> }) => void
  ) => {
    if (!guide) {
      toast.error(t("guides.notifications.updateContent.failed"))
      return
    }

    const updatedGuide = {
      ...guide,
      content: {
        ...guide.content,
        blocks: guide.content.blocks.map((block, idx) =>
          idx === selectedContentIndex ? { data: values, type: selectedType } : block
        )
      }
    }

    try {
      await updateGuideMutation.mutateAsync({ id: guide.id, data: updatedGuide })
      toast.success(t("guides.notifications.updateContent.success"))
      resetForm({ values })
    } catch {
      toast.error(t("guides.notifications.updateContent.failed"))
    }
  }

  const handleEditGuide = async (values: Record<string, string>) => {
    setShowEditTitleModal(false)

    if (guide?.id) {
      const updatedGuide = {
        ...guide,
        title: values.titleEn || values.titleFi,
        titleEn: values.titleEn,
        titleFi: values.titleFi,
        icon: values.icon,
        isDraft: values.isDraft
      }

      try {
        await updateGuideMutation.mutateAsync({ id: guide.id, data: updatedGuide })
        toast.success(t("guides.notifications.updateGuide.success"))
      } catch {
        toast.error(t("guides.notifications.updateGuide.failed"))
      }
    } else {
      toast.error(t("guides.notifications.updateGuide.failed"))
    }
  }

  const addTextContent = async (type: string) => {
    if (!guide) {
      toast.error(t("guides.notifications.updateContent.failed"))
      return
    }

    const emptyContent = {
      data: { en: { text: "", level: 2 }, fi: { text: "", level: 2 } },
      type
    }

    const updatedGuide = {
      ...guide,
      content: { ...guide.content, blocks: [...guide.content.blocks, emptyContent] }
    }

    try {
      const response = await updateGuideMutation.mutateAsync({ id: guide.id, data: updatedGuide })
      const index = response.content.blocks.length - 1
      const block = response.content.blocks[index]
      selectContent(index, block)
      toast.success(t("guides.notifications.addTextContent.success"))
    } catch {
      toast.error(t("guides.notifications.addTextContent.failed"))
    }
  }

  const handleAddTextContent = (type: string) => {
    if (!formRef.current?.dirty) {
      addTextContent(type)
    } else {
      setConfirmation({
        show: true,
        callback: () => {
          hideConfirmation()
          addTextContent(type)
        },
        title: "dirty",
        content: "dirty",
        danger: false
      })
    }
  }

  const handleAddImageContent = async (images: FileList) => {
    if (guide?.id) {
      const loadingToast = toast.loading(t("guides.notifications.addImageContent.uploading"))
      try {
        await addImageMutation.mutateAsync({ images: Array.from(images), guide })
        toast.dismiss(loadingToast)
        toast.success(t("guides.notifications.addImageContent.success"))
      } catch {
        toast.dismiss(loadingToast)
        toast.error(t("guides.notifications.addImageContent.failed"))
      }
    } else {
      toast.error(t("guides.notifications.addImageContent.failed"))
    }
  }

  const handleAddVideoContent = async (videos: FileList) => {
    if (guide?.id && videos) {
      const loadingToast = toast.loading(t("guides.notifications.addVideoContent.uploading"))
      try {
        await addVideoMutation.mutateAsync({ videos: Array.from(videos), guide })
        toast.dismiss(loadingToast)
        toast.success(t("guides.notifications.addVideoContent.success"))
      } catch {
        toast.dismiss(loadingToast)
        toast.error(t("guides.notifications.addVideoContent.failed.default"))
      }
    } else {
      toast.error(t("guides.notifications.addVideoContent.failed.default"))
    }
  }

  const handleDeleteContent = () => {
    setConfirmation({
      show: true,
      callback: async () => {
        hideConfirmation()

        if (guide?.id && selectedContentIndex !== null) {
          const updatedContent = {
            ...guide.content,
            blocks: guide.content.blocks.filter((_, i) => i !== selectedContentIndex)
          }

          try {
            await updateGuideMutation.mutateAsync({ id: guide.id, data: { content: updatedContent } })
            toast.success(t("guides.notifications.deleteContent.success"))
            setSelectedContentIndex(null)
            setSelectedContent(null)
            setSelectedType(null)
          } catch {
            toast.error(t("guides.notifications.deleteContent.failed"))
          }
        } else {
          toast.error(t("guides.notifications.deleteContent.failed"))
        }
      },
      title: "delete",
      content: "delete.content",
      danger: true
    })
  }

  return (
    <Container fluid className="mb-2">
      <GuideModal
        show={showEditTitleModal}
        data={guide}
        onSubmit={handleEditGuide}
        onCancel={() => setShowEditTitleModal(false)}
      />

      <ConfirmationModal
        show={confirmation.show}
        title={confirmation.title}
        content={confirmation.content}
        onOK={confirmation.callback}
        onCancel={hideConfirmation}
        danger={confirmation.danger}
      />

      <Row className="tw-mb-4">
        <Col>
          <div className="tw-flex tw-items-center tw-justify-between">
            <h3 className="tw-font-semibold">{t("guides.title")}</h3>
            {guide?.isLocked && (
              <div
                className="tw-inline-flex tw-items-center tw-rounded-lg tw-bg-yellow-50 tw-px-3 tw-py-1.5
                  tw-text-yellow-800 tw-shadow">
                <HiLockClosed className="tw-mr-2 tw-h-4 tw-w-4 tw-text-yellow-700" />
                <span>{t("guides.lockedNotice")}</span>
              </div>
            )}
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <Card>
            <Row>
              <Col xs={3}>
                {guide && (
                  <ListGroup className="h-100 br-2">
                    <div className="p-3 guide-list-header">
                      <Row>
                        <Col xs={12}>
                          <Nav
                            justify
                            defaultActiveKey={language}
                            onSelect={lng => setLanguage(lng ?? "en")}
                            className="mb-3 language-navigation">
                            <Nav.Item>
                              <Nav.Link eventKey="fi">{t("common.languages.fi")}</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                              <Nav.Link eventKey="en">{t("common.languages.en")}</Nav.Link>
                            </Nav.Item>
                          </Nav>
                        </Col>
                        <Col xs={12}>
                          <IonIcon name={guide.icon} style={undefined} />
                          <span className="ml-2 font-weight-bold">{getLocaleValue(guide, "title", language)}</span>
                          <Button
                            variant="link"
                            className="btn p-0 float-right text-primary"
                            onClick={() => setShowEditTitleModal(true)}>
                            <HiPencil className="icon mr-1" />
                            <span className="small">{t("guides.button.edit")}</span>
                          </Button>
                        </Col>
                      </Row>
                    </div>
                    {isLoading && (
                      <div className="loading-spinner">
                        <HiOutlineRefresh className="slow-spin icon loading-spinner-icon" />
                      </div>
                    )}
                    <DragDropProvider
                      modifiers={[RestrictToVerticalAxis]}
                      plugins={disableDropAnimation}
                      onDragEnd={event => {
                        if (!event.canceled) {
                          handleSortEnd(move(blocks, event))
                        }
                      }}>
                      {blocks.map((block, index) => (
                        <SortableContentItem
                          key={block.id}
                          id={block.id}
                          index={index}
                          block={block}
                          language={language}
                          selected={index === selectedContentIndex}
                          onClick={() => handleSelectContent(index, block)}
                        />
                      ))}
                    </DragDropProvider>
                    {!blocks.length && (
                      <ListGroup.Item>
                        <p className="text-muted text-center font-italic m-0 p-4">
                          {t("guides.selectedGuide.noContent")}
                        </p>
                      </ListGroup.Item>
                    )}
                    <ListGroup.Item className="h-100 d-flex flex-column justify-content-end">
                      <DropdownButton
                        title={t("guides.button.new")}
                        id="add-guide-content-dropdown"
                        disabled={
                          updateGuideMutation.isPending || addImageMutation.isPending || addVideoMutation.isPending
                        }
                        className="text-center m-2 dropdown-btn-block">
                        <Dropdown.Item onClick={() => handleAddTextContent("header")}>
                          {t("guides.button.dropdown.header")}
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => handleAddTextContent("paragraph")}>
                          {t("guides.button.dropdown.paragraph")}
                        </Dropdown.Item>
                        <FileUploader
                          handleFile={handleAddImageContent}
                          accept="image/*,.heic"
                          tooltip={t("guides.tooltips.image", { max_size: MAX_IMAGE_FILE_SIZE })}>
                          <Dropdown.Item>{t("guides.button.dropdown.image")}</Dropdown.Item>
                        </FileUploader>
                        <FileUploader
                          handleFile={handleAddVideoContent}
                          accept="video/*,.mkv,.3gp"
                          multiple={false}
                          tooltip={t("guides.tooltips.video", { max_size: MAX_VIDEO_FILE_SIZE })}>
                          <Dropdown.Item bsPrefix="dropdown-item text-prewrap-xl">
                            {t("guides.button.dropdown.video")}
                          </Dropdown.Item>
                        </FileUploader>
                      </DropdownButton>
                    </ListGroup.Item>
                  </ListGroup>
                )}
              </Col>
              <Col xs={9}>
                <div className="py-4 pr-4">
                  <Row className="mb-3">
                    <Col>{selectedType && <h5>{t("guides.selectedContent.title." + selectedType)}</h5>}</Col>
                    <Col>
                      {selectedContentIndex !== null && selectedContentIndex >= 0 && (
                        <Button
                          variant="link"
                          className="float-right text-danger"
                          disabled={isLoading}
                          onClick={handleDeleteContent}>
                          <HiTrash className="icon mr-1" />
                          {t("guides.button.delete")}
                        </Button>
                      )}
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      {selectedContent && selectedType === "image" && (
                        <GuideImage data={selectedContent as GuideImageData} list={false} preview={false} full />
                      )}
                      {selectedContent && selectedType === "video" && (
                        <GuideVideo data={selectedContent as VideoData} list={false} preview={false} full />
                      )}
                      {selectedContent && (selectedType === "header" || selectedType === "paragraph") && (
                        <Formik
                          innerRef={formRef as never}
                          key={"Formik-" + selectedContentIndex}
                          enableReinitialize={true}
                          initialValues={selectedContent}
                          validateOnBlur={false}
                          onSubmit={(values, { resetForm }) => handleSaveContent(values, resetForm)}>
                          {({ handleSubmit, handleReset, handleChange, handleBlur, values, ...props }) => (
                            <Form noValidate onSubmit={handleSubmit}>
                              {selectedType === "header" && (
                                <Row>
                                  <Form.Group as={Col} xs={12} xl={6} controlId="fi" className="position-relative">
                                    <Form.Text className="text-muted">{t("guides.form.titleFi")}</Form.Text>
                                    <Form.Control
                                      autoFocus
                                      type="text"
                                      name="fi.text"
                                      placeholder=""
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      value={(values as Record<string, Record<string, string>>)?.fi?.["text"] ?? ""}
                                      isInvalid={
                                        !!(props.touched as Record<string, unknown>).fi &&
                                        !!(props.errors as Record<string, unknown>).fi
                                      }
                                      className={
                                        (props.touched as Record<string, unknown>).fi &&
                                        (props.errors as Record<string, unknown>).fi
                                          ? "has-error"
                                          : undefined
                                      }
                                    />
                                    <Form.Control.Feedback tooltip type="invalid">
                                      {(props.errors as Record<string, string>).fi}
                                    </Form.Control.Feedback>
                                  </Form.Group>
                                  <Form.Group as={Col} xs={12} xl={6} controlId="en" className="position-relative">
                                    <Form.Text className="text-muted">{t("guides.form.titleEn")}</Form.Text>
                                    <Form.Control
                                      type="text"
                                      name="en.text"
                                      placeholder=""
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      value={(values as Record<string, Record<string, string>>)?.en?.["text"] ?? ""}
                                      isInvalid={
                                        !!(props.touched as Record<string, unknown>).en &&
                                        !!(props.errors as Record<string, unknown>).en
                                      }
                                      className={
                                        (props.touched as Record<string, unknown>).en &&
                                        (props.errors as Record<string, unknown>).en
                                          ? "has-error"
                                          : undefined
                                      }
                                    />
                                    <Form.Control.Feedback tooltip type="invalid">
                                      {(props.errors as Record<string, string>).en}
                                    </Form.Control.Feedback>
                                  </Form.Group>
                                </Row>
                              )}

                              {selectedType === "paragraph" && (
                                <Row>
                                  <Form.Group as={Col} xs={12} xl={6} controlId="fi" className="position-relative">
                                    <Form.Text className="text-muted">{t("guides.form.paragraphFi")}</Form.Text>
                                    <Form.Control
                                      as="textarea"
                                      rows={6}
                                      name="fi.text"
                                      placeholder=""
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      value={(values as Record<string, Record<string, string>>)?.fi?.["text"] ?? ""}
                                      isInvalid={
                                        !!(props.touched as Record<string, unknown>).fi &&
                                        !!(props.errors as Record<string, unknown>).fi
                                      }
                                      className={
                                        (props.touched as Record<string, unknown>).fi &&
                                        (props.errors as Record<string, unknown>).fi
                                          ? "has-error"
                                          : undefined
                                      }
                                      spellCheck={false}
                                    />
                                    <Form.Control.Feedback tooltip type="invalid">
                                      {(props.errors as Record<string, string>).fi}
                                    </Form.Control.Feedback>
                                  </Form.Group>
                                  <Form.Group as={Col} xs={12} xl={6} controlId="en" className="position-relative">
                                    <Form.Text className="text-muted">{t("guides.form.paragraphEn")}</Form.Text>
                                    <Form.Control
                                      as="textarea"
                                      rows={6}
                                      name="en.text"
                                      placeholder=""
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      value={(values as Record<string, Record<string, string>>)?.en?.["text"] ?? ""}
                                      isInvalid={
                                        !!(props.touched as Record<string, unknown>).en &&
                                        !!(props.errors as Record<string, unknown>).en
                                      }
                                      className={
                                        (props.touched as Record<string, unknown>).en &&
                                        (props.errors as Record<string, unknown>).en
                                          ? "has-error"
                                          : undefined
                                      }
                                      spellCheck={false}
                                    />
                                    <Form.Control.Feedback tooltip type="invalid">
                                      {(props.errors as Record<string, string>).en}
                                    </Form.Control.Feedback>
                                  </Form.Group>
                                </Row>
                              )}
                              {props.dirty && (
                                <div className="fixed-footer-bar">
                                  <div className="float-left">
                                    <span className="text-dark align-middle">{t("common.unsavedChanges.content")}</span>
                                  </div>
                                  <div className="float-right">
                                    <Button
                                      variant="link"
                                      onClick={handleReset}
                                      className="mr-4"
                                      type="reset"
                                      disabled={isLoading}>
                                      {t("common.unsavedChanges.button.cancel")}
                                    </Button>
                                    <Button
                                      variant="primary"
                                      className="btn-default-lg"
                                      type="submit"
                                      disabled={isLoading}>
                                      {t("common.unsavedChanges.button.save")}
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </Form>
                          )}
                        </Formik>
                      )}
                      {!selectedContent && (
                        <p className="text-muted font-italic mt-5 text-center">
                          {t("guides.selectedContent.unselected")}
                        </p>
                      )}
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </Container>
  )
}

export default EditGuideContainer
